/* ED desktop: an email-confirmed UI gate, not private-content authorization. */
(() => {
  const mount = (root, signal) => {
    const desktop = root.querySelector('[data-desktop]');
    const windowEl = root.querySelector('[data-stack-contents]');
    if (!desktop || !windowEl) return;
    const folders = [...desktop.querySelectorAll('[data-folder]')];
    const status = desktop.querySelector('[data-desktop-status]');
    const signup = root.querySelector('.prelaunch__signup');
    const email = root.querySelector('input[type="email"]');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const preview = root.hasAttribute('data-entry-preview');
    const storageKey = 'ed-desktop-access-v1';
    const confirmed = Boolean(root.querySelector('.prelaunch__glass-form.is-success'));
    let unlocked = confirmed;
    // Only a server-rendered Shopify success state creates access. Never trust URL parameters.
    try {
      if (!preview) {
        if (unlocked) sessionStorage.setItem(storageKey, '1');
        else unlocked = sessionStorage.getItem(storageKey) === '1';
      }
    } catch (_) { /* Confirmation still unlocks this page if storage is unavailable. */ }
    if (preview && root.dataset.desktopTestLocked === 'true') unlocked = false;
    let active = null, itemAnimations = [], shake = null, tooltipTimer, settleTimer, fadeFrame;
    const tooltip = document.createElement('span');
    tooltip.className = 'os-email-tooltip'; tooltip.hidden = true;
    tooltip.textContent = 'enter email'; tooltip.setAttribute('role', 'status');
    signup.append(tooltip);
    desktop.dataset.unlocked = String(unlocked);
    if (root.querySelector('.prelaunch__glass-form.is-success')) desktop.classList.add('is-unlocking');
    folders.forEach(folder => folder.setAttribute('aria-disabled', String(!unlocked)));
    status.textContent = unlocked ? 'Your desktop is unlocked.' : 'Subscribe to unlock your desktop.';
    const settleDesktop = (immediate = false) => {
      desktop.classList.add('is-centered');
      signup.inert = true;
      signup.setAttribute('aria-hidden', 'true');
      const started = performance.now();
      const duration = immediate ? 0 : reduced.matches ? 120 : 450;
      const fade = now => {
        if (signal.aborted) return;
        const progress = duration ? Math.min(1, (now - started) / duration) : 1;
        root.style.setProperty('--desktop-signup-opacity', String(1 - progress));
        if (progress < 1) fadeFrame = requestAnimationFrame(fade);
        else { signup.hidden = true; }
      };
      if (immediate) {
        desktop.classList.add('is-settled');
        fade(started);
      } else fadeFrame = requestAnimationFrame(fade);
    };
    if (unlocked) {
      if (confirmed) settleTimer = setTimeout(() => settleDesktop(), 1100);
      else settleDesktop(true);
    }

    const promptEmail = () => {
      email?.focus({ preventScroll: true });
      shake?.cancel(); clearTimeout(tooltipTimer); tooltip.hidden = true;
      // Move the HTML and its shader geometry together; no doubled capsule.
      const duration = reduced.matches ? 1 : 440;
      shake = signup.animate([0, -10, 10, -8, 8, -4, 4, 0].map(x => ({
        transform: `translate(calc(-50% + ${x}px), -50%)`,
      })), { duration, easing: 'ease-in-out' });
      window.EDLiquidGlass?.get(root)?.invalidateGeometry(600);
      const current = shake;
      current.finished.then(() => {
        if (signal.aborted || shake !== current) return;
        tooltip.hidden = false;
        tooltipTimer = setTimeout(() => { tooltip.hidden = true; }, 3000);
      }).catch(() => {});
    };
    const panels = new Map();
    windowEl.hidden = false;
    desktop.classList.add('has-real-stacks');
    const arrange = () => {
      const container = windowEl.getBoundingClientRect();
      folders.forEach(folder => {
        const panel = panels.get(folder);
        const origin = folder.querySelector('.os-folder__art').getBoundingClientRect();
        const cx = origin.left + origin.width/2, cy = origin.top + origin.height/2;
        const cards = [...panel.querySelectorAll('.os-file,.os-social-link')];
        // Stable irregular offsets: a loose pile, never a new random layout on resize.
        const pile = [[0,0,-4],[-9,-5,7],[8,-9,-8],[-4,5,4],[10,2,-2],[-6,-11,-6],[5,8,8],[-11,3,2],[3,-13,-3],[11,-6,5]];
        const scale = unlocked ? .94 : .72;
        const columns = Math.min(innerWidth >= 600 ? 3 : 2,cards.length);
        const rows = Math.ceil(cards.length/columns);
        const expandedScale = Math.min(1.3,(innerWidth-48-(columns-1)*18)/(112*columns),(innerHeight-180-(rows-1)*18)/(64*rows));
        const stepX = 112*expandedScale+18, stepY = 64*expandedScale+18;
        cards.forEach((item,index) => {
          item.style.left = (cx-container.left-56) + 'px';
          item.style.top = (cy-container.top-32) + 'px';
          item.style.zIndex = String(10-index);
          const [dx,dy,angle] = pile[index%pile.length];
          const countInRow = Math.min(columns,cards.length-Math.floor(index/columns)*columns);
          const x = innerWidth/2+(index%columns-(countInRow-1)/2)*stepX;
          const centerY = Math.max(90+rows*stepY/2,Math.min(innerHeight-100-rows*stepY/2,cy-90));
          const y = centerY+(Math.floor(index/columns)-(rows-1)/2)*stepY;
          item.dataset.collapsed = `translate(${dx*scale}px,${dy*scale}px) scale(${scale}) rotate(${angle}deg)`;
          item.dataset.expanded = `translate(${x-cx}px,${y-cy}px) scale(${expandedScale}) rotate(0deg)`;
          item.style.transform = active === folder ? item.dataset.expanded : item.dataset.collapsed;
        });
      });
    };
    const setExpanded = (folder, expanded, immediate = false) => {
      const panel = panels.get(folder);
      panel.inert = !expanded;
      panel.setAttribute('aria-hidden', String(!expanded));
      panel.dataset.expanded = String(expanded);
      folder.classList.toggle('is-open',expanded);
      folder.setAttribute('aria-expanded',String(expanded));
      const cards = [...panel.querySelectorAll('.os-file,.os-social-link')];
      cards.forEach((item,index) => {
        item.getAnimations().filter(animation => animation.id === 'stack-motion-blur').forEach(animation => animation.cancel());
        if (!immediate && !reduced.matches) {
          const blur = item.animate([
            {filter:'blur(0px)'},
            {filter:'blur(1.8px)',offset:.3},
            {filter:'blur(.6px)',offset:.65},
            {filter:'blur(0px)'}
          ], {duration:650,delay:(expanded ? index : cards.length-1-index)*45,easing:'ease-out'});
          blur.id = 'stack-motion-blur';
          itemAnimations.push(blur);
          blur.finished.finally(() => { itemAnimations = itemAnimations.filter(animation => animation !== blur); }).catch(() => {});
        }
        item.style.transitionDuration = immediate || reduced.matches ? '0ms' : '650ms';
        item.style.transitionDelay = immediate || reduced.matches ? '0ms' : ((expanded ? index : cards.length-1-index)*45)+'ms';
        item.style.transform = expanded ? item.dataset.expanded : item.dataset.collapsed;
      });
      if (!expanded) {
        const info = panel.querySelector('[data-release-info]');
        if (info) { info.hidden = true; panel.querySelector('[data-release-toggle]').setAttribute('aria-expanded','false'); }
      }
    };
    const close = (restoreFocus = false, immediate = false) => {
      if (!active) return;
      const previous = active; active = null;
      setExpanded(previous,false,immediate);
      if (restoreFocus) previous.focus({preventScroll:true});
    };
    const open = folder => {
      if (!unlocked) { promptEmail(); return; }
      if (active === folder) { close(true); return; }
      close();
      arrange();
      active = folder;
      setExpanded(folder,true);
    };
    folders.forEach(folder => {
      const panel = windowEl.querySelector('[data-folder-panel="' + folder.dataset.folder + '"]');
      panels.set(folder,panel);
      panel.hidden = false;
      panel.id = 'Stack-' + folder.dataset.folder + '-' + (root.id || 'preview');
      folder.setAttribute('aria-controls',panel.id);
      folder.addEventListener('click', () => open(folder), {signal});
    });
    arrange();
    folders.forEach(folder => setExpanded(folder,false,true));
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape' && active) { event.preventDefault(); close(true); }
    }, {signal});
    document.addEventListener('click', event => {
      if (active && !desktop.contains(event.target)) close();
    }, {signal});
    window.addEventListener('resize', () => { close(false,true); arrange(); }, {signal});
    const release = windowEl.querySelector('[data-release-toggle]');
    const info = windowEl.querySelector('[data-release-info]');
    release.addEventListener('click', () => {
      const expanded = release.getAttribute('aria-expanded') !== 'true';
      release.setAttribute('aria-expanded', String(expanded)); info.hidden = !expanded;
      if (expanded) info.animate([{ opacity: 0, transform: 'translateY(-8px)' }, { opacity: 1, transform: 'none' }], { duration: reduced.matches ? 1 : 280, easing: 'ease-out' });
    }, { signal });
    email?.addEventListener('input', () => { tooltip.hidden = true; clearTimeout(tooltipTimer); }, { signal });
    signal.addEventListener('abort', () => {
      shake?.cancel(); itemAnimations.forEach(animation => animation.cancel()); clearTimeout(tooltipTimer);
      clearTimeout(settleTimer); cancelAnimationFrame(fadeFrame);
      close(false, true); tooltip.remove();
    }, { once: true });
  };
  window.EDDesktop = Object.freeze({ mount });
})();
